import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import InstitutionNavbar from '../components/instituteNavbar';
import Footer from "../components/Footer";

import {
    Briefcase, MapPin, Clock, Calendar, IndianRupee,
    CheckCircle, ArrowLeft, Mail, Layout, Phone
} from 'lucide-react';
import "../styles/GlobalStyles.css";

const ViewJobDetails = () => {
    const { id } = useParams();
    const [job, setJob] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchJob = async () => {
            try {
                const token = localStorage.getItem("token");
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/institutionfilter/getJob/${id}`, {
                    headers: { Authorization: `Bearer ${token}` },
                    withCredentials: true
                });
                setJob(response.data.data);
            } catch (err) {
                console.error("Failed to fetch job", err);
                setError("Failed to load job details.");
            } finally {
                setLoading(false);
            }
        };

        if (id) fetchJob();
    }, [id]);

    if (loading) return (
        <div className="loading-container">
            <div className="loading-text">Loading job details...</div>
        </div>
    );

    if (error) return (
        <div className="error-container">
            <div className="error-text">{error}</div>
            <Link to="/InstitutionHomepage" className="link-back">Go Back</Link>
        </div>
    );

    if (!job) return null;

    // Check if job is closed based on date
    const isClosed = job.closing_date && new Date(job.closing_date) < new Date();

    const formatDate = (dateString) => dateString ? new Date(dateString).toLocaleDateString() : 'N/A';

    return (
        <div className="global-font page-wrapper">
            <InstitutionNavbar />

            <div
                className="view-job-container mt-[120px]"
            >

                {/* Header */}
                <div className="view-job-header">
                    <div className="flex items-center gap-4">
                        <Link to="/InstitutionHomepage" className="btn-back-outline inline-flex items-center gap-2 px-4 py-2">
                            <ArrowLeft size={16} /> Back to Dashboard
                        </Link>
                        {isClosed && (
                            <span className="text-red-600 font-bold bg-red-50 px-4 py-2 rounded-lg border border-red-200">
                                Job is closed please repost
                            </span>
                        )}
                    </div>
                    <div className="flex gap-2">
                        {isClosed ? (
                            <Link to={`/post-job?id=${job._id}&mode=repost`} className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition font-semibold">
                                Repost Job
                            </Link>
                        ) : (
                            <Link to={`/post-job?id=${job._id}&mode=edit`} className="save-button border-radius: 50%">
                                Edit Job
                            </Link>
                        )}
                    </div>
                </div>

                <div className="view-job-grid">
                    {/* Main Content Column */}
                    <div className="space-y-6">
                        {/* Title Card */}
                        <div className="detail-card">
                            <div className="flex items-start justify-between">
                                <div>
                                    <span className={`status-badge ${isClosed || job.status !== 'open' ? 'closed' : 'open'}`}>
                                        {isClosed ? 'CLOSED' : job.status?.toUpperCase()}
                                    </span>
                                    <h1 className="job-title-large">{job.title}</h1>
                                    <div className="job-meta-row">
                                        <div className="job-meta-item">
                                            <Briefcase size={16} />
                                            {job.department}
                                        </div>
                                        <div className="job-meta-item">
                                            <MapPin size={16} />
                                            {job.address?.area},{job.address?.city}, {job.address?.state}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Job Overview */}
                        <div className="detail-card">
                            <h2 className="detail-section-title">Job Overview</h2>
                            <div className="detail-row">
                                <div className="detail-group">
                                    <label className="detail-label">Role</label>
                                    <span className="detail-value">{job.role_teaching || job.role_non_teaching || job.role}</span>
                                </div>
                                <div className="detail-group">
                                    <label className="detail-label">Employment Type</label>
                                    <span className="detail-value">{job.employment_type?.replace('_', ' ')}</span>
                                </div>
                                <div className="detail-group">
                                    <label className="detail-label">Experience</label>
                                    <span className="detail-value">{job.min_experience} - {job.max_experience || 'N/A'} Years</span>
                                </div>
                                <div className="detail-group">
                                    <label className="detail-label">Domain</label>
                                    <span className="detail-value">{job.domain}</span>
                                </div>
                            </div>
                            <div className="detail-row">
                                <div className="detail-group">
                                    <label className="detail-label">Category</label>
                                    <span className="detail-value">{job.category}</span>
                                </div>
                                <div className="detail-group">
                                    <label className="detail-label">Board</label>
                                    <span className="detail-value">{job.board || 'N/A'}</span>
                                </div>
                                <div className="detail-group">
                                    <label className="detail-label">Subject</label>
                                    <span className="detail-value">{job.subject || 'N/A'}</span>
                                </div>
                            </div>
                        </div>

                        {/* Description */}
                        <div className="detail-card">
                            <h2 className="detail-section-title">Description</h2>
                            <p className="description-text">
                                {job.description}
                            </p>
                        </div>

                        {/* Requirements & Responsibilities */}
                        <div className="detail-card">
                            <div className="req-grid">
                                <div>
                                    <h3 className="section-subtitle">
                                        <CheckCircle size={20} className="text-blue-600" /> Requirements
                                    </h3>
                                    <ul className="list-standard">
                                        {Array.isArray(job.requirements) && job.requirements.length > 0 ? (
                                            job.requirements.map((req, i) => (
                                                <li key={i} className="list-item">
                                                    <span className="list-bullet"></span>
                                                    {req}
                                                </li>
                                            ))
                                        ) : (
                                            <p className="text-italic-gray">No specific requirements listed.</p>
                                        )}
                                    </ul>
                                </div>
                                <div>
                                    <h3 className="section-subtitle">
                                        <Layout size={20} className="text-blue-600" /> Responsibilities
                                    </h3>
                                    <ul className="list-standard">
                                        {Array.isArray(job.responsibilities) && job.responsibilities.length > 0 ? (
                                            job.responsibilities.map((resp, i) => (
                                                <li key={i} className="list-item">
                                                    <span className="list-bullet"></span>
                                                    {resp}
                                                </li>
                                            ))
                                        ) : (
                                            <p className="text-italic-gray">No specific responsibilities listed.</p>
                                        )}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Side Column */}
                    <div className="space-y-6">
                        {/* Status Card */}
                        <div className="side-card bg-white">
                            <div className="space-y-4">
                                <div className="detail-group">
                                    <label className="detail-label">Posted Date</label>
                                    <div className="sidebar-text-group">
                                        <Calendar size={16} className="icon-gray" />
                                        {formatDate(job.posted_date)}
                                    </div>
                                </div>
                                <div className="detail-group">
                                    <label className="detail-label">Closing Date</label>
                                    <div className="sidebar-text-group">
                                        <Clock size={16} className="icon-gray" />
                                        {formatDate(job.closing_date)}
                                    </div>
                                </div>
                                <div className="detail-group">
                                    <label className="detail-label">Salary</label>
                                    <div className="sidebar-text-group-medium">
                                        <IndianRupee size={16} className="icon-gray" />
                                        {((job.salary_range?.lakh || 0) * 100000 + (job.salary_range?.thousand || 0) * 1000).toLocaleString('en-IN')}
                                    </div>
                                    {!job.salary_range?.is_visible_to_applicants && (
                                        <span className="alert-text-small">Hidden from applicants</span>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Contact Card */}
                        <div className="side-card bg-white">
                            <h3 className="side-card-title">Contact Info</h3>
                            <div className="space-y-4">
                                <div className="contact-row">
                                    <Mail size={18} className="icon-blue" />
                                    <div>
                                        <p className="sidebar-label-small">Email</p>
                                        <p className="sidebar-value-small">{job.contact_email || 'N/A'}</p>
                                    </div>
                                </div>
                                <div className="contact-row">
                                    <Phone size={18} className="icon-blue" />
                                    <div>
                                        <p className="sidebar-label-small">Phone</p>
                                        <p className="sidebar-value-small">{job.contact_phone || 'N/A'}</p>
                                    </div>
                                </div>
                                <div className="contact-row">
                                    <MapPin size={18} className="icon-blue" />
                                    <div>
                                        <p className="sidebar-label-small">Location</p>
                                        <p className="sidebar-value-small">
                                            {job.address?.street}, {job.address?.area} <br />
                                            {job.address?.city}, {job.address?.state} - {job.address?.postal_code}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Skills Tags */}
                        <div className="side-card bg-white">
                            <h3 className="side-card-title">Skills & Benefits</h3>
                            <div className="space-y-4">
                                <div>
                                    <p className="detail-label mb-2">Benefits</p>
                                    <div className="tag-list">
                                        {Array.isArray(job.benefits) && job.benefits.length > 0 ? (
                                            job.benefits.map((b, i) => <span key={i} className="badge-pill">{b}</span>)
                                        ) : <span className="text-sm text-gray-500">None</span>}
                                    </div>
                                </div>
                                <div>
                                    <p className="detail-label mb-2">Qualifications</p>
                                    <div className="tag-list">
                                        {Array.isArray(job.prefered_qualifications) && job.prefered_qualifications.length > 0 ? (
                                            job.prefered_qualifications.map((qual, i) => (
                                                <span key={i} className="badge-pill gray">{qual}</span>
                                            ))
                                        ) : (
                                            job.prefered_qualifications && typeof job.prefered_qualifications === 'string' ? (
                                                // Fallback for legacy data if stored as string
                                                <span className="badge-pill gray">{job.prefered_qualifications}</span>
                                            ) : (
                                                <span className="text-sm text-gray-500">None</span>
                                            )
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
            {/* Footer */}
            <Footer />

        </div>
    );
};

export default ViewJobDetails;
